import { patientsApi } from '../patientApi';
import { DefaultQueryParams } from '../patients.types';
import {
  PaymentProfileProps,
  PaymentProfileRes,
  PaymentProfileResponse,
  PaymentTransactionsResponseProps,
  RetryPaymentRequestProps
} from './patientPayment.types';

export const patientPaymentApi = patientsApi.injectEndpoints({
  endpoints: (builder) => ({
    createPaymentProfile: builder.mutation<
      PaymentProfileRes,
      { body: PaymentProfileProps | string; isInsuranceSubscription?: boolean; patientId: string }
    >({
      invalidatesTags: (_result, _error, arg) => [
        { id: arg.patientId, type: 'Patient' },
        'Billing',
        'PaymentProfiles'
      ],
      query: ({ body, isInsuranceSubscription, patientId }) => ({
        body,
        method: 'POST',
        url: `/patients/${patientId}/payment-profile${isInsuranceSubscription ? '?isInsuranceSubscription=true' : ''}`
      }),
      transformResponse: (response: { data: PaymentProfileRes }) => response.data
    }),
    deletePaymentProfile: builder.mutation<
      { message: string },
      { isInsuranceSubscription?: boolean; patientId: string; paymentProfileId: string }
    >({
      invalidatesTags: (_result, _error, arg) => [
        { id: arg.patientId, type: 'Patient' },
        'Billing',
        'PaymentProfiles'
      ],
      query: ({ isInsuranceSubscription, patientId, paymentProfileId }) => ({
        method: 'DELETE',
        url: `/patients/${patientId}/payment-profile/${paymentProfileId}${isInsuranceSubscription ? '?isInsuranceSubscription=true' : ''}`
      })
    }),
    getAccountBalances: builder.query<{ creditBalance: string }, { patientId: string }>({
      query: ({ patientId }) => `/patients/${patientId}/account-balances`,
      transformResponse: (response: { data: { creditBalance: string } }) => response.data
    }),
    getPaymentProfile: builder.query<PaymentProfileResponse, { patientId: string }>({
      providesTags: ['PaymentProfiles'],
      query: ({ patientId }) => `/patients/${patientId}/payment-profiles`,
      transformResponse: (response: { data: PaymentProfileResponse }) => response.data
    }),
    getPaymentTransactions: builder.query<
      PaymentTransactionsResponseProps,
      { id: string; params: DefaultQueryParams }
    >({
      providesTags: ['PaymentTransactions'],
      query: ({ id, params }) => {
        return {
          params,
          url: `/patients/${id}/payment-transactions`
        };
      }
    }),
    retryPayment: builder.mutation<{ message: string }, RetryPaymentRequestProps>({
      invalidatesTags: (_result, _error, arg) => [
        { id: arg.patientId, type: 'Patient' },
        'Billing'
      ],
      query: ({ currentProgramId, patientId }) => ({
        body: { currentProgramId },
        method: 'PUT',
        url: `/patients/${patientId}/retry-subscription-payment`
      })
    }),
    submitInsurance: builder.mutation({
      invalidatesTags: ['Benefits'],
      query: ({ body, patientId }) => ({
        body,
        method: 'POST',
        url: `/patients/${patientId}/submit-insurance`
      })
    }),
    updateDefaultPaymentProfile: builder.mutation<
      { message: string },
      { isInsuranceSubscription?: boolean; patientId: string; paymentProfileId: string }
    >({
      invalidatesTags: (_result, _error, arg) => [
        { id: arg.patientId, type: 'Patient' },
        'PaymentProfiles'
      ],
      query: ({ isInsuranceSubscription, patientId, paymentProfileId }) => ({
        method: 'PUT',
        url: `/patients/${patientId}/payment-profile/${paymentProfileId}/set-default${isInsuranceSubscription ? '?isInsuranceSubscription=true' : ''}`
      })
    }),
    updatePaymentProfile: builder.mutation<
      PaymentProfileRes,
      {
        body:
          | { chargifyToken: string }
          | {
              cvv: string;
              expirationMonth: string;
              expirationYear: string;
              fullNumber: string;
            };
        isInsuranceSubscription: boolean;
        patientId: string;
        paymentProfileId: string;
      }
    >({
      invalidatesTags: (_result, _error, arg) => [
        { id: arg.patientId, type: 'Patient' },
        { id: 'LIST', type: 'Billing' },
        'PaymentProfiles'
      ],
      query: ({ body, isInsuranceSubscription, patientId, paymentProfileId }) => ({
        body,
        method: 'PUT',
        url: `/patients/${patientId}/payment-profile/${paymentProfileId}${isInsuranceSubscription ? '?isInsuranceSubscription=true' : ''}`
      }),
      transformResponse: (response: { data: PaymentProfileRes }) => response.data
    })
  })
});

export const {
  useCreatePaymentProfileMutation,
  useDeletePaymentProfileMutation,
  useGetAccountBalancesQuery,
  useGetPaymentProfileQuery,
  useLazyGetPaymentTransactionsQuery,
  useRetryPaymentMutation,
  useSubmitInsuranceMutation,
  useUpdateDefaultPaymentProfileMutation,
  useUpdatePaymentProfileMutation
} = patientPaymentApi;
